/**
 * Outbound Shipments list for admin / staff / sales ("Outbound Shipments Table" design): who gets it and how a
 * row reads. Pure helpers, so the rules can be tested without rendering the page.
 *
 * Columns are the staff ones OrdersTableColumns has for `outbound_shipments`, in the same order and with the
 * same filter / sort params, so every request is the one the old page sent. Customers (their own Shipments
 * route) and warehouse users keep the old page.
 */

import { authRoles } from 'app/auth';
import { SUPPLIER_STATUS_TONES } from 'app/main/apps/supplier/list/supplierListUi';

function roleList(user) {
  const role = user?.role;
  if (Array.isArray(role)) return role.filter(Boolean);
  return role ? [role] : [];
}

/** Admin, staff or sales (authRoles.staff), as the staff lists. Customer and warehouse keep the old page. */
export function shouldUseOutboundList(user) {
  const staff = authRoles?.staff || [];
  return roleList(user).some((role) => staff.includes(role));
}

/** Only admins select rows, to delete them (the old page's selection column and Delete Shipment). */
export function outboundCanDelete(user) {
  return roleList(user).includes('admin');
}

/* ------------------------------------------------------------------ columns */

const column = (key, label, width, extra = {}) => ({ key, label, width, align: 'left', kind: 'text', filter: null, sort: null, ...extra });

const textCol = (key, label, width, field, extra = {}) =>
  column(key, label, width, { value: (o) => o?.[field], filter: { param: field, input: 'text' }, sort: field, ...extra });

// Weight, No of orders and No of pallets never sorted or filtered on the old page; they still don't.
const countCol = (key, label, width, field) => column(key, label, width, { align: 'right', kind: 'int', value: (o) => o?.[field] });

export const OUTBOUND_LIST = {
  stage: 'outbound_shipments',
  title: 'Outbound Shipments',
  noun: 'shipments',
  actionSlots: 4,
  columns: [
    // PGSS-5767: wide enough for "SHIP-3317" and the Priority mark side by side (at 150 they overlapped).
    column('no', 'Shipment no', 190, {
      pin: true,
      kind: 'record',
      value: (o) => o?.shipment_no,
      flag: (o) => (o?.is_priority ? 'Priority' : null),
      flagTitle: 'Priority shipment',
      filter: { param: 'shipment_no', input: 'text' },
      sort: 'shipment_no',
    }),
    textCol('customer', 'Customer name', 190, 'customer_name'),
    column('date', 'Shipment date', 126, {
      kind: 'date', value: (o) => o?.shipment_date, filter: { param: 'shipment_date', input: 'date' }, sort: 'shipment_date',
    }),
    textCol('location', 'Location', 160, 'location'),
    countCol('weight', 'Weight (lbs)', 104, 'weight'),
    countCol('orders', 'No of orders', 96, 'no_of_orders'),
    column('type', 'Shipment type', 136, {
      kind: 'type', value: (o) => o?.shipment_type, filter: { param: 'shipment_type', input: 'shipmentType' }, sort: 'shipment_type',
    }),
    countCol('pallets', 'No of pallets', 98, 'pallet_count'),
    column('tracking', 'Tracking #', 170, {
      kind: 'tracking',
      value: (o) => o?.tracking_number,
      url: (o) => o?.tracking_url || null,
      carrier: (o) => o?.carrier || null,
      filter: { param: 'tracking_number', input: 'text' },
      sort: 'tracking_number',
    }),
    column('status', 'Status', 230, {
      fixed: true, kind: 'status', value: (o) => o?.status?.name, filter: { param: 'status', input: 'status' }, sort: 'status',
    }),
  ],
};

/* -------------------------------------------------------------------- tones */

/**
 * Status tone by whose move it is for staff: an approved shipment is theirs to dispatch, a revision or a
 * rejection is theirs to fix, awaiting approval is the customer's. Anything unlisted reads as waiting.
 */
export const OUTBOUND_STATUS_TONE_BY_CODE = {
  SHIPMENT_APPROVED: 'act',
  SHIPMENT_REVISION_REQUESTED: 'rev',
  SHIPMENT_COMPLETED: 'ok',
  SHIPMENT_REJECTED: 'stop',
};

export function outboundToneKey(systemCode) {
  return OUTBOUND_STATUS_TONE_BY_CODE[systemCode] || 'wait';
}

export function outboundStatusTone(systemCode) {
  return SUPPLIER_STATUS_TONES[outboundToneKey(systemCode)];
}

/* ------------------------------------------------------------------ actions */

/** Staff's next step on a row: shown first and highlighted. Matched on the action's name (its tooltip). */
export const OUTBOUND_PRIMARY_ACTIONS = ['ready for dispatch'];

export function isOutboundPrimaryAction(label) {
  return OUTBOUND_PRIMARY_ACTIONS.includes(String(label || '').trim().toLowerCase());
}

/**
 * Whether the page must load the shipment types itself. Edit Shipment's form (ShipmentFormContent) takes its
 * required Shipment Type options from backofficeApp.shipmentOrder.shipmentTypeNames, which the old page filled
 * on mount; opened straight from the URL, nothing else has.
 */
export function outboundNeedsShipmentTypes(shipmentTypeNames) {
  return !Array.isArray(shipmentTypeNames) || shipmentTypeNames.length === 0;
}

/* ---------------------------------------------------------------- selection */

export const outboundShipmentId = (shipment) => shipment?.shipment_id ?? shipment?.id;

/** Selection after ticking (on) or unticking one shipment. Kept as the shipment rows, as the old page kept them. */
export function outboundToggle(selected, shipment, on) {
  const id = outboundShipmentId(shipment);
  const rest = selected.filter((s) => outboundShipmentId(s) !== id);
  return on ? [...rest, shipment] : rest;
}

/** Selection after the header box: every shipment on the page in (on) or out. */
export function outboundToggleAll(selected, pageRows, on) {
  const ids = new Set(pageRows.map(outboundShipmentId));
  const rest = selected.filter((s) => !ids.has(outboundShipmentId(s)));
  return on ? [...rest, ...pageRows] : rest;
}
